import * as React from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { cn } from "@/lib/utils";
import { Input } from "@/ui/components/ui/input";
import { Button } from "@/ui/components/ui/button";
import {
  forgotPasswordSchema,
  type ForgotPasswordFormData,
} from "@/schemas/passwordResetSchemas";

export interface ForgotPasswordFormProps {
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Callback when form is submitted
   */
  onSubmit: (data: ForgotPasswordFormData) => Promise<void>;
  /**
   * Loading state during request
   */
  isLoading?: boolean;
  /**
   * Global error message
   */
  error?: string | null;
  /**
   * Callback when "Back to Login" is clicked
   */
  onBackToLogin: () => void;
}

/**
 * ForgotPasswordForm Component
 *
 * Form for requesting password reset code via email.
 * Follows LoginForm pattern with Zod validation and i18n.
 *
 * @example
 * ```tsx
 * <ForgotPasswordForm
 *   onSubmit={async (data) => console.log(data)}
 *   isLoading={false}
 *   error={null}
 *   onBackToLogin={() => navigate('/auth/login')}
 * />
 * ```
 */
export const ForgotPasswordForm = React.forwardRef<
  HTMLFormElement,
  ForgotPasswordFormProps
>(({ className, onSubmit, isLoading = false, error, onBackToLogin }, ref) => {
  const { t } = useTranslation("auth");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  return (
    <div
      className={cn(
        "bg-white box-border flex flex-col gap-4 items-center px-4 py-2 rounded-[0.3125rem] w-full",
        className
      )}
    >
      {/* Title */}
      <div className="flex flex-col gap-[0.5rem] w-full">
        <h2 className="font-montserrat font-semibold text-gray-900 text-xl leading-7 w-full">
          {t("passwordReset.forgot.title")}
        </h2>
        <p className="font-montserrat font-normal text-[0.9375rem] text-gray-700 tracking-[0.3px] leading-5 w-full">
          {t("passwordReset.forgot.subtitle")}
        </p>
      </div>

      {/* Global Error Banner */}
      {error && (
        <div
          role="alert"
          className="w-full bg-red-100 border border-red-200 text-brand-red rounded-[0.3125rem] px-4 py-3 text-sm"
        >
          {error}
        </div>
      )}

      {/* Form */}
      <form
        ref={ref}
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-4 w-full"
      >
        {/* Email Input */}
        <div className="flex flex-col gap-1 w-full">
          <Input
            type="email"
            variant="anyw3ar"
            placeholder={t("passwordReset.forgot.emailPlaceholder")}
            disabled={isLoading}
            aria-label={t("passwordReset.forgot.email")}
            aria-invalid={errors.email ? "true" : "false"}
            className={cn(
              errors.email && "border-brand-red focus:ring-brand-red"
            )}
            {...register("email")}
          />
          {errors.email && (
            <span className="text-brand-red text-sm" role="alert">
              {t(errors.email.message as string)}
            </span>
          )}
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          variant="primary-red"
          size="primary-red"
          className="w-full"
          disabled={isLoading}
        >
          {isLoading
            ? t("passwordReset.forgot.submitting")
            : t("passwordReset.forgot.submit")}
        </Button>

        {/* Back to Login Link */}
        <button
          type="button"
          onClick={onBackToLogin}
          className="font-montserrat font-normal text-[0.9375rem] text-brand-red text-center tracking-[0.3px] leading-5 hover:underline focus:outline-none focus:underline disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isLoading}
        >
          {t("passwordReset.forgot.backToLogin")}
        </button>
      </form>
    </div>
  );
});

ForgotPasswordForm.displayName = "ForgotPasswordForm";
